import React from 'react';
import { ClearableSelect } from '../clearable-select';

const ALL_CATEGORIES = 'all';

interface IcpCategorySelectProps {
  /**
   * Map of category IDs to category names
   * @required
   * @type {Record<string, string>}
   */
  categories: Record<string, string>;
  /**
   * The currently selected category ID
   * @optional
   * @type {string}
   */
  selectedCategoryId?: string | null;
  /**
   * Whether the categories are still loading
   * @optional
   * @type {boolean}
   * @default false
   */
  isLoading?: boolean;
  /**
   * Callback fired when the category selection changes
   * @required
   * @type {(categoryId: string | null) => void}
   */
  onCategoryChange: (categoryId: string | null) => void;
}

/**
 * Select component for choosing an ICP product category filter.
 * Allows users to filter organizations by their ICP product categories.
 *
 * @param props - The component props
 * @param props.categories - Map of category IDs to display names
 * @param props.selectedCategoryId - The currently selected category ID
 * @param props.isLoading - Whether the categories are loading
 * @param props.onCategoryChange - Callback fired when the category changes
 */
function IcpCategorySelect(props: IcpCategorySelectProps) {
  const { categories, selectedCategoryId, isLoading = false, onCategoryChange } = props;

  const selectedKey = React.useMemo(
    () => (selectedCategoryId && selectedCategoryId !== ALL_CATEGORIES ? selectedCategoryId : null),
    [selectedCategoryId],
  );

  const items = React.useMemo(() => {
    return Object.entries(categories)
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([id, name]) => ({ id, label: name }));
  }, [categories]);

  return (
    <ClearableSelect
      ariaLabel="ICP product category"
      placeholder={isLoading ? 'Loading categories...' : 'All categories'}
      items={items}
      selectedKey={selectedKey}
      onSelectionChange={onCategoryChange}
    />
  );
}

export { IcpCategorySelect };
export type { IcpCategorySelectProps };
